// Copyright (c) 2021 embyt GmbH. All rights reserved.
// Author: Roman Morawek

// also defined in mqttmessages.ts, opccommon.ts
export enum AlarmSource {
  // alarms triggered by controller
  TargetValue = "NVAlarm_Targetvalue",
  Restart = "NVAlarm_Restart",
  MissingSync = "NVAlarm_MissingSync",
  Modul = "NVAlarm_Modul",
  ManualActive = "NVAlarm_ManualActive",
  // only used in Loytec devices
  EnergyMeter = "NVAlarm_EnergyMeter",
  Linx = "NVAlarm_LINX",
  // alarms triggered in cloud
  Offline = "Cloud_Offline",
  TrendMonitor = "Cloud_TrendMonitor",
  OcppChargerOffline = "Cloud_ChargerOffline",
  OcppChargerFaulted = "Cloud_ChargerFaulted",
  OcppChargingInterrupted = "Cloud_ChargingInterrupted",
}

export enum AlarmState {
  ACTIVE = 0, // alarm is active and not acknowledged
  ACK_PENDING = 1, // alarm is inactive but not acknowledged
  ACKD_ACTIVE = 2, // alarm is active and acknowledged
  ACKD = 3, // alarm is inactive and acknowledged
}

export enum DeviceType {
  LINX_120 = "LINX-120",
  LINX_153 = "LINX-153",
  LIOB_586 = "LIOB-586",
  EMBYT_PC = "embyt-pc",
  EMBYT_STRATO = "embyt-strato",
  EMBYT_EAP = "embyt-eap",
  VIRTUAL = "virtual",
  CLOUD = "cloud",
}

export interface AlarmSettings {
  offlineTimeout?: number; // s, until site offline alert triggers; zero to disable
  chargerOfflineTimeout?: number; // s, until ocpp charger offline alert triggers; zero to disable
  chargerFaultedTimeout?: number; // s, until ocpp charger connector failure alert triggers; zero to disable
  chargingInterruptedSoc?: number; // %, charging interrupted alarm SoC limit; zero to disable
  emailAddress?: string;
  emailEnabled?: {
    [source: string]: boolean;
  };
}

export interface EngineSettings {
  recurrence?: number;
}

export interface ReportsSettings {
  endCustomer?: string;
  orderNumber?: string;
  serviceType?: string;
  annotation?: string; // comment
}

export enum DeletionStrategy {
  SMALL = "small",
  LARGE = "large",
  UNLIMITED = "unlimited",
}

export interface CloudSettings {
  alarm?: AlarmSettings;
  reports?: Report[];
  reportsSettings?: ReportsSettings;
  trendMonitor?: TrendMonitor[];
  engine?: EngineSettings;
  deletionStrategy?: DeletionStrategy;
  customDashboard?: any; // see CustomDashboard interface in parameter.ts
}

export interface Site {
  id: number;
  name: string;
  serial: string;
  company: string | undefined;
  deviceType: DeviceType;
  deviceSw: string | undefined;
  tzOffset: number | undefined;
  cloudSettings: CloudSettings | undefined;
}

export interface UserSettings {
  showChargerTariff?: boolean;
}

export interface UserAccessRights {
  setVirtualInputs?: boolean;
  switchOutputs?: boolean;
  setParameter?: boolean;
  ackAlarms?: boolean;
  ocppSettings?: boolean; // RFIDs, tariffs, chargers public/private
  ocppChargerAdmin?: boolean; // delete charger, change connector names
  ocppChargerCtrl?: boolean; // start/stop transactions, unlock connector, reboot charger
  createReports?: boolean; // can create reports
  crudTrendMonitors?: boolean; // create/read/update/delete trend-monitors
  modifyAlarms?: boolean; // activate/modify alarms
  importTrendData?: boolean; // allowed to upload trend files
  showEnlineChargers?: boolean; // show energy management chargers (independent of ocpp chargers)
  controllerAdmin?: boolean; // show extended controller data (modules, conditions) and allow reboot
  openSshTunnel?: boolean; // no gui to set this access right, need to modify db directly
}

export interface RfidAccessRights {
  isLoginAllowed?: boolean;
  isChargerPowerCtrlAllowed?: boolean; // set charging power limits and strategy
}

// keep consistent with ModalRfidSettings.tsx
export enum eRfidLimitPeriod {
  DAY = "DAY",
  MONTH = "MONTH",
  YEAR = "YEAR",
}

export enum UserRole {
  Viewer = "viewer",
  Admin = "admin",
  Root = "root",
}

export interface User {
  id: string;
  role: UserRole;
  name: string;
  company: string | undefined;
  accessRights: UserAccessRights;
  siteIds: number[];
}

export enum FileUploadStatus {
  INITIAL = "initial",
  RECEIVED = "received",
  PROCESSED = "processed",
  ERROR = "error",
}

// definitions for data reports
export enum TrendingColum {
  ENERGY = "energy",
  ENERGY_AVG = "energy_avg",
  ENERGY_MIN = "energy_min",
  ENERGY_MAX = "energy_max",
  TARIFF_ENERGY = "tariff_energy",
  MAX_ENERGY = "max_energy",
  TARGET_VALUE = "target_value",
  MAX_POWER = "max_power",
  COST = "cost",
  TARIFF_COST = "tariff_cost",
  PEAK_COST = "peak_cost",
  ENERGYMETER = "energymeter",
}

export enum Period {
  HOUR = "hour",
  DAY = "day",
  MONTH = "month",
  YEAR = "year",
}
export enum ExtendedPeriod {
  LAST_DAY = "last_day",
  LAST_MONTH = "last_month",
  LAST_YEAR = "last_year",
}

// keep consistent with parameter.ts::ICloudReportColumnDefinition
export interface ICloudReportColumnDefinition {
  title: string;
  value: string;
  unit?: string;
  decimals?: number;
}

interface ReportContent {
  customColumn?: ICloudReportColumnDefinition[];
}

export interface IReportFilter {
  type: string;
  ref?: number | Period | ExtendedPeriod; // for reference filters; Period for period, else number
}

export interface ITrendReportFilter extends IReportFilter {
  type: "period" | "begin" | "end" | "circuit";
}

export type TrendReportContent = ReportContent & {
  groupBy: ("date" | "circuit")[];
  aggregation: Period | "none";
  columns: TrendingColum[];
  filter: ITrendReportFilter[];
  reference:
    | {
        type: "date" | "circuit";
        ref: number | Period; // Period for period, number for circuit
      }
    | undefined;
  hideEmpty: boolean;
  isEvuFormat?: boolean;
};

export interface AutoExport {
  target: string;
  interval: Period | "none";
}

export enum ChargerColum {
  ENERGY = "energy",
  DURATION = "duration",
  RFID = "rfid",
  USER = "user",
  VEHICLE = "vehicle",
  CHARGER = "charger",
  CONNECTOR = "connector",
  METER_VALUE = "meter_value",
  STOP_METER_VALUE = "stop_meter_value",
  COSTS = "costs",
  TARIFF = "tariff",
  STOP_REASON = "stop_reason",
  CATEGORY = "category",
  ADDITIONAL_DATA = "additional_data",
}

export interface IChargerReportFilter extends IReportFilter {
  type: "period" | "begin" | "end" | "category" | "rfiduser";
  value?: string; // for category or rfiduser filter
}

export type ChargerReportContent = ReportContent & {
  columns: ChargerColum[];
  filter: IChargerReportFilter[];
};

export interface IIoReportFilter extends IReportFilter {
  type: "period" | "begin" | "end" | "input" | "output";
}

export type IoReportContent = ReportContent & {
  aggregation: Period | "none";
  filter: IIoReportFilter[];
};

export type SiteReportContent = ReportContent & {
  company: string | undefined;
  filter: IReportFilter[]; // currently unused
};

export interface Report {
  title: string;
  isActive: boolean;
  type: "trend" | "charger" | "io" | "site";
  content: TrendReportContent | ChargerReportContent | IoReportContent | SiteReportContent;
  autoExport: AutoExport;
  // ensure to have this consistent with sheet-js supported output format (BookType)
  fileFormat?: "xlsx" | "xls" | "ods" | "csv" | "txt" | "html" | "rtf";
  csvDelimiter?: ";" | "," | "\t" | "|";
  hideSumRow?: boolean;
}

export interface TrendMonitor {
  title: string;
  isActive: boolean;
  circuit: number;
  period: Period;
  targetValue?: number; // fixed number, not used for reference periods
  referencePeriod?: Period; // the relative distance for the reference, not used for fixed target values
  tolerance: number; // the overshoot tolerance for the alarm trigger
  slope: number; // similar to "Abschaltgerade" of regulation, the slope of the alarm trigger from end of period
}

export enum ChargingMode {
  CostOptimized = "costOptimized",
  Early = "early",
  Late = "late",
  PVOptimized = "pvOptimized",
  Uniform = "uniform",
}

export interface IUserChargingControl {
  isActive: boolean;
  useAtNewSession: boolean;
  mode: ChargingMode;
  maxPower: number | undefined;
  targetEnergy: number | undefined;
  targetTimestamp: number | undefined;
}

/* defines references in a virtual parameter assignement */
// also refer to parameter.ts, which describes the resolved definition in the API/parameter set
export interface IVirtSiteDefinition {
  input?: IVirtDefinition[];
  energymeter?: IVirtDefinition[];
  tariffline?: IVirtDefinition[];
  regulation?: IVirtDefinition[];
  output?: IVirtDefinition[];
  charger?: IVirtDefinition[];
  ocpp?: IVirtDefinition[];
}

export interface IVirtDefinition {
  sourceSite: number;
  // either sourceIndex, sourceId or sourceName must be set
  sourceIndex?: number; // array index of the source
  sourceId?: number; // id of the source
  sourceName?: string; // regular expression to match source name; not allowed for ocpp chargers
  // possibilities to overwrite source parameter, e.g. its name
  name?: string;
  isEnabled?: boolean;
  webActive?: boolean;
  webCtrl?: boolean;
}

/*
 * DB constant definitions
 */

// epex id of t_tariff_variable, not of t_tariff!
export const EPEX_TARIFF_ID = 1;

// define maximum number of data-entries for requesting ALL trend-data
// after aggregated-data was queried...
export const IO_TREND_LIMIT = 20000;

export enum ChargerStatusError {
  // ocpp codes
  CONNECTOR_LOCK_FAILURE = "ConnectorLockFailure",
  EV_COMMUNICATION_ERROR = "EVCommunicationError",
  GROUND_FAILURE = "GroundFailure",
  HIGH_TEMPERATURE = "HighTemperature",
  INTERNAL_ERROR = "InternalError",
  LOCAL_LIST_CONFLICT = "LocalListConflict",
  NO_ERROR = "NoError",
  OTHER_ERROR = "OtherError",
  OVER_CURRENT_FAILURE = "OverCurrentFailure",
  POWER_METER_FAILURE = "PowerMeterFailure",
  POWER_SWITCH_FAILURE = "PowerSwitchFailure",
  READER_FAILURE = "ReaderFailure",
  RESET_FAILURE = "ResetFailure",
  UNDER_VOLTAGE = "UnderVoltage",
  OVER_VOLTAGE = "OverVoltage",
  WEAK_SIGNAL = "WeakSignal",
  //backend codes
  CHARGER_COMMUNICATION_ERROR = "ChargerCommunicationError",
  BOOT_NOTIFICATION = "BootNotification",
}

// connection type NULL indicates direct ocpp connection
export enum ChargerConnectionType {
  CLOUD = "cloud",
  PROXY = "proxy",
  PROXY_SECONDARY = "proxy-secondary",
  GATEWAY = "gateway",
  GATEWAY_SECONDARY = "gateway-secondary",
}

export interface IDataSubscription {
  subChargerStatusData?: boolean | null; // subscribes to charging session and charger status notification data
  subChargerPowerData?: boolean | null; // subscribes to dynamic charger data like current power, voltage,...
  subControllerPowerData?: boolean | null; // subscribes to input/output/energy data of energy management controller
  subControllerChargerData?: boolean | null; // deprecated: subscribes to charger data routed via energy management controller
}

export interface IFileImportResultTariffArgs {
  energyMeter: string;
  tariffs: number[];
  importedEntries: number;
}

export interface IFileImportResultArg {
  status: FileUploadStatus;
  message?: string;
  notFoundEMs?: string[];
  succeededEMs?: IFileImportResultTariffArgs[];
  multipleEMsFound?: string[];
  noTariffsFound?: string[];
  duration?: number;
}
